/** The tour itinerary as draft records (plan §3 D7, §5): one record per day, stop
 *  (timeslot) and activity row, with membership lists for the structure.
 *
 *  - `tour:<id>` · `itinerary.days` — the day ids in order; `itinerary.kml` — the tour-level
 *    route file (B6: stored `{url}`, a staged file token, or null = clear).
 *  - `day:<id>` · `day.title`, `day.subtitle`, `day.description` (HTML), `day.day_span`,
 *    `day.slots` (the stop ids of the day), `day.kml` (the day's route file).
 *  - `slot:<id>` · `slot.time` ("HH:MM"), `slot.title`, `slot.subtitle`, `slot.description`,
 *    `slot.timeline_display`, `slot.timeline_text`, `slot.show_transport`,
 *    `slot.next_transport` (a transport type id), `slot.transport_text`, `slot.activities`
 *    (the activity row ids), `slot.pin` (the map pin — out of band, `slot_pins`).
 *  - `activity:<id>` · `activity.activity` (the catalog activity's id), the price cluster
 *    (`activity.cost_prefix`, `activity.cost_min` / `cost_max` as typed text,
 *    `activity.cost_unit`, `activity.currency`), `activity.display_name`,
 *    `activity.display_on_map`.
 *
 *  A negative id is a row the draft creates. A stop's order within its day is its TIME —
 *  the server orders a day's timeslots by time — so `day.slots` is a set and every view and
 *  the compile sort it by time; an activity's order is its position in `slot.activities`.
 *
 *  The levels are the records outline's (`TOUR_OUTLINE`, `records/outline/tree.ts`): the
 *  values, the tree walk and the structural operations are its, bound here to the tour.
 *  Pure (no React): projection from the detail, defaults, values, the tree walk the store,
 *  the validation and the compile share. */
import type { Itinerary, ItineraryActivity, ItineraryDay, ItinerarySlot, PinType, TourDetail } from '@/api/tours';
import { parseRecordKey, recordKey, type DraftState, type RecordKey } from '@/features/records/draft/changeSet';
import {
  listIn as outlineListIn,
  outlineDefault,
  outlineTree,
  valueIn as outlineValueIn,
  type Baselines,
  type Entries,
  type OutlineSpec,
} from '@/features/records/outline/tree';

export const DAY = 'day';
export const SLOT = 'slot';
export const ACTIVITY = 'activity';
export const ITINERARY_ENTITIES: ReadonlySet<string> = new Set([DAY, SLOT, ACTIVITY]);

export const DAYS_PATH = 'itinerary.days';
export const TOUR_KML = 'itinerary.kml';
export const DAY_SLOTS = 'day.slots';
export const DAY_KML = 'day.kml';
export const SLOT_ACTIVITIES = 'slot.activities';
export const SLOT_PIN = 'slot.pin';

/** The itinerary's membership lists (merged on a refetch, never "drifted") — part of the
 *  tour record's `MEMBERSHIP_PATHS` (`record/collections.ts`). */
export const ITINERARY_LISTS: ReadonlySet<string> = new Set([DAYS_PATH, DAY_SLOTS, SLOT_ACTIVITIES]);

export const dayRec = (id: number): RecordKey => recordKey(DAY, id);
export const slotRec = (id: number): RecordKey => recordKey(SLOT, id);
export const activityRec = (id: number): RecordKey => recordKey(ACTIVITY, id);

export function isItineraryRec(rec: RecordKey): boolean {
  return ITINERARY_ENTITIES.has(parseRecordKey(rec).entity);
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const TIMELINE_DISPLAY: { value: number; label: string }[] = [
  { value: 0, label: 'Label' },
  { value: 1, label: 'Time' },
  { value: 2, label: 'Hide' },
];

/** The 12 standard map-pin icon keys (dashboard `mapPinConfig` parity — the guest app
 *  renders these keys; their icons live in `PinPopover`). */
export const PIN_ICON_KEYS = [
  'meeting_point',
  'cafes',
  'restaurants',
  'bars',
  'accommodation',
  'transportation',
  'shopping',
  'activity',
  'nature',
  'sightseeing',
  'culture',
  'events',
] as const;

export interface PinValue {
  pin_type: PinType;
  pin_number_mode: 'sequential' | 'custom' | null;
  pin_custom_number: string;
  pin_icon_key: string | null;
}

export interface PinPayload {
  pin_type: PinType;
  pin_number_mode: string | null;
  pin_custom_number: string | null;
  pin_icon_key: string | null;
}

export const DEFAULT_PIN: PinValue = {
  pin_type: 'numbered',
  pin_number_mode: 'sequential',
  pin_custom_number: '',
  pin_icon_key: null,
};

export function pinOf(slot: Pick<ItinerarySlot, 'pin_type' | 'pin_number_mode' | 'pin_custom_number' | 'pin_icon_key'>): PinValue {
  const icon = slot.pin_type === 'icon';
  return {
    pin_type: icon ? 'icon' : 'numbered',
    pin_number_mode: icon ? null : (slot.pin_number_mode ?? 'sequential'),
    pin_custom_number: slot.pin_custom_number ?? '',
    pin_icon_key: slot.pin_icon_key ?? null,
  };
}

export function asPin(v: unknown): PinValue {
  if (typeof v !== 'object' || v === null) return DEFAULT_PIN;
  return { ...DEFAULT_PIN, ...(v as Partial<PinValue>) };
}

/** What `slot_pins` carries for a pin. */
export function pinPayload(pin: PinValue): PinPayload {
  if (pin.pin_type === 'numbered') {
    const custom = pin.pin_number_mode === 'custom';
    return {
      pin_type: 'numbered',
      pin_number_mode: custom ? 'custom' : 'sequential',
      pin_custom_number: custom ? pin.pin_custom_number : null,
      pin_icon_key: null,
    };
  }
  return { pin_type: 'icon', pin_number_mode: null, pin_custom_number: null, pin_icon_key: pin.pin_icon_key };
}

export function pinsEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(pinPayload(asPin(a))) === JSON.stringify(pinPayload(asPin(b)));
}

export function pinLabel(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** "#3" (sequential), "#7" (custom), "Cafes" (icon). */
export function pinSummary(v: unknown, position: number | null): string {
  const pin = asPin(v);
  if (pin.pin_type === 'icon') return pin.pin_icon_key ? pinLabel(pin.pin_icon_key) : 'Icon';
  if (pin.pin_number_mode === 'custom') return `#${pin.pin_custom_number || '?'}`;
  return position == null ? 'Numbered' : `#${position}`;
}

/** "HH:MM:SS" | "HH:MM" → "HH:MM". */
export function hhmm(time: string | null | undefined): string {
  if (!time) return '';
  const m = /^(\d{1,2}):(\d{2})/.exec(time);
  if (!m) return '';
  return `${m[1]!.padStart(2, '0')}:${m[2]}`;
}

export const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

// ---------------------------------------------------------------------------
// Fields and defaults
// ---------------------------------------------------------------------------

export const DAY_DEFAULTS: Readonly<Record<string, unknown>> = {
  'day.title': null,
  'day.subtitle': null,
  'day.description': null,
  'day.day_span': 1,
  [DAY_SLOTS]: [],
  [DAY_KML]: null,
};

export const NEW_SLOT_TIME = '09:00';

export const SLOT_DEFAULTS: Readonly<Record<string, unknown>> = {
  'slot.time': NEW_SLOT_TIME,
  'slot.title': null,
  'slot.subtitle': null,
  'slot.description': null,
  'slot.timeline_display': 1,
  'slot.timeline_text': null,
  'slot.show_transport': false,
  'slot.next_transport': null,
  'slot.transport_text': null,
  [SLOT_ACTIVITIES]: [],
  [SLOT_PIN]: DEFAULT_PIN,
};

export const ACTIVITY_DEFAULTS: Readonly<Record<string, unknown>> = {
  'activity.activity': null,
  'activity.cost_prefix': null,
  'activity.cost_min': null,
  'activity.cost_max': null,
  'activity.cost_unit': null,
  'activity.currency': null,
  'activity.display_name': null,
  'activity.display_on_map': false,
};

export const ITINERARY_DEFAULTS: Readonly<Record<string, Readonly<Record<string, unknown>>>> = {
  [DAY]: DAY_DEFAULTS,
  [SLOT]: SLOT_DEFAULTS,
  [ACTIVITY]: ACTIVITY_DEFAULTS,
};

/** The itinerary's levels on the records outline: days (`itinerary.days` on the tour
 *  record), a day's stops in time order, a stop's activity rows. */
export const TOUR_OUTLINE: OutlineSpec = [
  { entity: DAY, list: DAYS_PATH, defaults: DAY_DEFAULTS },
  { entity: SLOT, list: DAY_SLOTS, defaults: SLOT_DEFAULTS, orderBy: 'slot.time' },
  { entity: ACTIVITY, list: SLOT_ACTIVITIES, defaults: ACTIVITY_DEFAULTS },
];

/** A new row's value of a path (undefined: not an itinerary path). */
export function itineraryDefault(rec: RecordKey, path: string): unknown {
  return outlineDefault(TOUR_OUTLINE, rec, path);
}

// ---------------------------------------------------------------------------
// Projection (detail → baselines)
// ---------------------------------------------------------------------------

export interface ItineraryRaw {
  days: Map<number, ItineraryDay>;
  slots: Map<number, ItinerarySlot>;
  activities: Map<number, ItineraryActivity>;
}

export interface ProjectedItinerary {
  /** `day:<id>` / `slot:<id>` / `activity:<id>` → path → value. */
  paths: Record<RecordKey, Record<string, unknown>>;
  /** The days in order (the tour record's `itinerary.days`). */
  days: number[];
  /** The tour-level route file (`itinerary.kml`). */
  kml: { url: string } | null;
  raw: ItineraryRaw;
}

const text = (v: string | null | undefined): string | null => (v === undefined || v === '' ? null : v);

export function projectSlot(slot: ItinerarySlot): Record<string, unknown> {
  return {
    'slot.time': hhmm(slot.time),
    'slot.title': text(slot.title),
    'slot.subtitle': text(slot.subtitle),
    'slot.description': text(slot.description),
    'slot.timeline_display': slot.timeline_display ?? 0,
    'slot.timeline_text': text(slot.timeline_text),
    'slot.show_transport': Boolean(slot.show_transport),
    'slot.next_transport': slot.next_transport?.id ?? null,
    'slot.transport_text': text(slot.transport_text),
    [SLOT_ACTIVITIES]: (slot.activities ?? []).map((a) => a.id),
    [SLOT_PIN]: pinOf(slot),
  };
}

export function projectActivity(a: ItineraryActivity): Record<string, unknown> {
  return {
    'activity.activity': a.activity?.id ?? null,
    'activity.cost_prefix': text(a.cost_prefix),
    'activity.cost_min': text(a.cost_min),
    'activity.cost_max': text(a.cost_max),
    'activity.cost_unit': text(a.cost_unit),
    'activity.currency': a.currency ?? null,
    'activity.display_name': text(a.display_name),
    'activity.display_on_map': Boolean(a.display_on_map),
  };
}

export function sortedDays(itin: Pick<Itinerary, 'days'> | null | undefined): ItineraryDay[] {
  return [...(itin?.days ?? [])].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0) || a.id - b.id);
}

export function projectItinerary(detail: Pick<TourDetail, 'itinerary'>): ProjectedItinerary {
  const paths: Record<RecordKey, Record<string, unknown>> = {};
  const raw: ItineraryRaw = { days: new Map(), slots: new Map(), activities: new Map() };
  const days = sortedDays(detail.itinerary);
  for (const day of days) {
    raw.days.set(day.id, day);
    paths[dayRec(day.id)] = {
      'day.title': text(day.title),
      'day.subtitle': text(day.subtitle),
      'day.description': text(day.description),
      'day.day_span': day.day_span ?? 1,
      [DAY_SLOTS]: (day.timeslots ?? []).map((s) => s.id),
      [DAY_KML]: day.kml_file ? { url: day.kml_file } : null,
    };
    for (const slot of day.timeslots ?? []) {
      raw.slots.set(slot.id, slot);
      paths[slotRec(slot.id)] = projectSlot(slot);
      for (const a of slot.activities ?? []) {
        raw.activities.set(a.id, a);
        paths[activityRec(a.id)] = projectActivity(a);
      }
    }
  }
  const kml = detail.itinerary?.kml_file ? { url: detail.itinerary.kml_file } : null;
  return { paths, days: days.map((d) => d.id), kml, raw };
}

// ---------------------------------------------------------------------------
// Values over the draft
// ---------------------------------------------------------------------------

export type { Baselines, Entries };

/** A path's value: the draft's (edit mode), else the stored one, else a new row's
 *  default. */
export function valueIn(entries: Entries | null, baselines: Baselines, rec: RecordKey, path: string): unknown {
  return outlineValueIn(TOUR_OUTLINE, entries, baselines, rec, path);
}

export function listIn(entries: Entries | null, baselines: Baselines, rec: RecordKey, path: string): number[] {
  return outlineListIn(TOUR_OUTLINE, entries, baselines, rec, path);
}

export interface TreeSlot {
  id: number;
  activities: number[];
}

export interface TreeDay {
  id: number;
  /** The day's stops in time order. */
  slots: TreeSlot[];
}

/** The itinerary as the draft (or, with `entries` null, the stored tour) has it: days in
 *  order, each day's stops in time order, each stop's activities in order. */
export function itineraryTree(entries: Entries | null, baselines: Baselines, tourRec: RecordKey): TreeDay[] {
  return outlineTree(TOUR_OUTLINE, entries, baselines, tourRec).map((day) => ({
    id: day.id,
    slots: day.children.map((slot) => ({ id: slot.id, activities: slot.children.map((a) => a.id) })),
  }));
}

/** Where each row sits in a tree: a stop's day, an activity's stop and day. */
export interface Placement {
  days: Map<number, number>;
  slots: Map<number, { dayId: number; dayIndex: number; slotIndex: number }>;
  activities: Map<number, { slotId: number; dayId: number; dayIndex: number }>;
}

export function placementOf(tree: readonly TreeDay[]): Placement {
  const out: Placement = { days: new Map(), slots: new Map(), activities: new Map() };
  tree.forEach((day, dayIndex) => {
    out.days.set(day.id, dayIndex);
    day.slots.forEach((slot, slotIndex) => {
      out.slots.set(slot.id, { dayId: day.id, dayIndex, slotIndex });
      for (const a of slot.activities) out.activities.set(a, { slotId: slot.id, dayId: day.id, dayIndex });
    });
  });
  return out;
}

const placements = new WeakMap<object, Placement>();

/** `placementOf` the draft's tree, cached per draft state (the ledger asks per row). */
export function draftPlacement(state: Pick<DraftState, 'entries'>, baselines: Baselines, tourRec: RecordKey): Placement {
  const key = state.entries as object;
  let hit = placements.get(key);
  if (!hit) {
    hit = placementOf(itineraryTree(state.entries, baselines, tourRec));
    placements.set(key, hit);
  }
  return hit;
}

/** The day a record belongs to in a placement (a day itself, a stop's, an activity's). */
export function dayOfRec(rec: RecordKey, placement: Placement): { dayId: number; dayIndex: number } | null {
  const { entity, id } = parseRecordKey(rec);
  if (entity === DAY) {
    const dayIndex = placement.days.get(id);
    return dayIndex === undefined ? null : { dayId: id, dayIndex };
  }
  if (entity === SLOT) {
    const p = placement.slots.get(id);
    return p ? { dayId: p.dayId, dayIndex: p.dayIndex } : null;
  }
  if (entity === ACTIVITY) {
    const p = placement.activities.get(id);
    return p ? { dayId: p.dayId, dayIndex: p.dayIndex } : null;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Names in a sentence
// ---------------------------------------------------------------------------

// Text of stored HTML — shared with the record editor's rich-text widget.
export { htmlText } from '@/components/RichText/htmlText';

export function newRowName(id: number): string {
  return `NEW-${-id}`;
}
